import React, { useState } from 'react';
import { FocusOn } from 'react-focus-on';
import classnames from 'classnames';
import { TextLink } from '../link';

import styles from './header-old.module.scss';
import { LinkType } from '../types';

export type NavigationItemOldType = (
  | LinkType
  | {
      firstLevellabel: string;
      secondLevelOverviewLink: LinkType;
      secondLevelSubItems: LinkType[];
    }
)[];

export type HeaderOldType = {
  navigationAriaLabel?: string;
  openMenuAriaLabel: string;
  closeMenuAriaLabel: string;
  backToFirstLevelAriaLabel?: string;
  logoLink: LinkType;
  navigationItems?: NavigationItemOldType;
  className?: string;
  cta?: LinkType;
  myGarageLink?: LinkType;
  myAccountLink?: LinkType;
};

const isLinkNavigationItem = (props: unknown): props is LinkType =>
  typeof (props as LinkType)?.label === 'string' && typeof (props as LinkType)?.href === 'string';

export const HeaderOld: React.FC<HeaderOldType> = ({
  className,
  cta,
  navigationItems,
  navigationAriaLabel,
  logoLink,
  openMenuAriaLabel,
  closeMenuAriaLabel,
  backToFirstLevelAriaLabel = closeMenuAriaLabel,
  myAccountLink,
  myGarageLink
}) => {
  const [flyoutIsOpen, setFlyoutIsOpen] = useState<boolean>(false);
  const [openedSecondLevelIndex, setOpenedSecondLevelIndex] = useState<number | false>(false);

  const classes = classnames(
    styles.header,
    className,
    {
      [styles['header--without-navigation']]: !navigationItems || navigationItems.length === 0,
      [styles['flyout--is-open']]: flyoutIsOpen,
      [styles['secondLevelIsOpen']]: openedSecondLevelIndex
    },
    'setting--inverted'
  );

  const generateIconButton = (): {
    ariaLabel: string;
    iconClass: string;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
  } => {
    if (flyoutIsOpen && !openedSecondLevelIndex) {
      return {
        ariaLabel: closeMenuAriaLabel,
        iconClass: 'close',
        onClick: () => setFlyoutIsOpen(false)
      };
    }

    if (openedSecondLevelIndex) {
      return {
        ariaLabel: backToFirstLevelAriaLabel,
        iconClass: 'chevronLeft',
        onClick: () => setOpenedSecondLevelIndex(false)
      };
    }

    return { ariaLabel: openMenuAriaLabel, iconClass: 'burgerMenu', onClick: () => setFlyoutIsOpen(true) };
  };

  const closeMenu = () => {
    setOpenedSecondLevelIndex(false);
    setFlyoutIsOpen(false);
  };

  const menuButton = generateIconButton();

  return (
    <header className={classes} aria-label={navigationAriaLabel} data-nosnippet="true">
      <FocusOn enabled={flyoutIsOpen} onEscapeKey={closeMenu} onClickOutside={closeMenu}>
        <div className={styles.inner}>
          {navigationItems && navigationItems.length !== 0 && (
            <>
              <button
                aria-label={menuButton.ariaLabel}
                onClick={menuButton.onClick}
                aria-expanded={flyoutIsOpen}
                aria-controls="smart-header-id"
                className={classnames(styles.menuButton, styles[menuButton.iconClass])}
              />
              <nav className={styles.flyout} id="smart-header-id" aria-hidden={!flyoutIsOpen}>
                <ul className={styles['flyout__list']}>
                  {navigationItems.map((item, index) => {
                    if (isLinkNavigationItem(item)) {
                      return (
                        <li key={item.label + index}>
                          <TextLink
                            {...item}
                            variant="hyperlink-100"
                            className={classnames(styles.link, { [styles.isExternal]: item.isExternal })}
                            linkText={item.label}
                          />
                        </li>
                      );
                    }
                    return (
                      <li key={item.firstLevellabel + index} className={styles['flyout__item']}>
                        <button
                          className={classnames(styles.navItemButton, {
                            [styles.isActive]: openedSecondLevelIndex === index
                          })}
                          onClick={() => setOpenedSecondLevelIndex(index)}
                        >
                          {item.firstLevellabel}
                        </button>
                        <div
                          aria-hidden={openedSecondLevelIndex !== index}
                          className={classnames(styles.secondLevel, {
                            [styles.isOpen]: openedSecondLevelIndex === index
                          })}
                        >
                          <TextLink
                            {...item.secondLevelOverviewLink}
                            variant="hyperlink-100"
                            className={styles.link}
                            linkText={item.secondLevelOverviewLink.label}
                          />

                          <ul className={styles.secondLevelList}>
                            {item.secondLevelSubItems.map((subItem, i) => (
                              <li key={subItem.label + i}>
                                <TextLink
                                  {...subItem}
                                  variant="hyperlink-100"
                                  className={classnames(styles.link, {
                                    [styles.isExternal]: subItem.isExternal
                                  })}
                                  linkText={subItem.label}
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </>
          )}
          <a
            aria-label={logoLink.label}
            className={styles.logoLink}
            href={logoLink.href}
            rel={logoLink.rel}
            target={logoLink.target}
            onClick={logoLink.onClick}
          ></a>
          {(myAccountLink || myGarageLink) && (
            <div className={styles.garageAccountContainer}>
              {myGarageLink && (
                <a
                  className={styles.garageLink}
                  href={myGarageLink.href}
                  aria-label={myGarageLink.label}
                  onClick={myGarageLink.onClick}
                  target={myGarageLink.target}
                  rel={myGarageLink.rel}
                />
              )}
              {myAccountLink && (
                <a
                  className={styles.accountLink}
                  href={myAccountLink.href}
                  aria-label={myAccountLink.label}
                  onClick={myAccountLink.onClick}
                  target={myAccountLink.target}
                  rel={myAccountLink.rel}
                />
              )}
            </div>
          )}
          {cta && <TextLink {...cta} variant="button-primary" className={styles.cta} size="200" linkText={cta.label} />}
        </div>
      </FocusOn>
      <div className={classnames(styles.layer, { [styles['layer--is-open']]: flyoutIsOpen })} />
    </header>
  );
};
